import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useAddGroupMembers, useListGroupMemberships } from "@/queries/group-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, useMemo, type FC } from "react";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Group } from "gitpod-next-api/gitpod/v1/group_pb";
import { Principal } from "gitpod-next-api/gitpod/v1/identity_pb";
import { MemberSelectionList, type Subject } from "@/components/settings/organization/groups/MemberSelectionList";

type AddGroupMembersDialogProps = {
    open: boolean;
    onClose: () => void;
    group: PlainMessage<Group>;
};

export const AddGroupMembersDialog: FC<AddGroupMembersDialogProps> = ({ open, onClose, group }) => {
    const { toast } = useToast();
    const { data: groupMemberships = [] } = useListGroupMemberships(group.id);

    const [selectedSubjects, setSelectedSubjects] = useState<Map<string, Subject>>(new Map());

    // Exclude group IDs for server-side filtering of users
    const excludeGroupIds = useMemo(() => [group.id], [group.id]);

    // Service account IDs already in the group (for client-side filtering since exclude_group_ids only filters users)
    const currentServiceAccountIds = useMemo(() => {
        return new Set(
            groupMemberships
                .filter((m) => m.subject?.principal === Principal.SERVICE_ACCOUNT)
                .map((m) => m.subject?.id)
                .filter((id): id is string => Boolean(id)),
        );
    }, [groupMemberships]);

    const addMembersMutation = useAddGroupMembers();

    const handleSubmit = useCallback(() => {
        if (selectedSubjects.size === 0) return;

        // Convert Map values to array of subjects
        const subjects = Array.from(selectedSubjects.values());

        addMembersMutation.mutate(
            { groupId: group.id, subjects },
            {
                onSuccess: () => {
                    toast({
                        title: "Members added",
                        description: `${selectedSubjects.size} member${selectedSubjects.size === 1 ? "" : "s"} added to "${group.name}".`,
                    });

                    // Reset form
                    setSelectedSubjects(new Map());
                    onClose();
                },
                onError: (error) => {
                    toast({
                        title: "Failed to add members",
                        description: formatError(error),
                    });
                },
            },
        );
    }, [selectedSubjects, group, addMembersMutation, toast, onClose]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                // Reset form when dialog closes
                setSelectedSubjects(new Map());
                onClose();
            }
        },
        [onClose],
    );

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                data-testid="add-group-members-dialog"
                data-track-location={TrackLocations.CreateProjectModal}
                className="flex max-h-[90vh] h-[35rem] max-w-2xl flex-col overflow-hidden"
            >
                <DialogHeader>
                    <DialogTitle>
                        <span className="truncate">Add members to {group.name}</span>
                    </DialogTitle>
                    <DialogDescription>
                        Select organization members or service accounts to add to this group. Members will gain access
                        to group resources and permissions.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="flex min-h-0 flex-1 flex-col space-y-4 overflow-hidden">
                    <MemberSelectionList
                        excludeGroupIds={excludeGroupIds}
                        excludeSubjectIds={currentServiceAccountIds}
                        selectedSubjects={selectedSubjects}
                        onChangeSelection={setSelectedSubjects}
                        showSearch={true}
                        className="flex min-h-0 flex-1 flex-col space-y-2"
                    />
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="add-members-dialog-cancel">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        onClick={handleSubmit}
                        variant="primary"
                        disabled={selectedSubjects.size === 0 || addMembersMutation.isPending}
                        loading={addMembersMutation.isPending}
                        data-testid="add-members-dialog-add"
                        data-tracking-id="add-members-dialog-add"
                    >
                        Add
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
